'use client';

import { faArrowLeft, faArrowRight, faCalendar, faClock, faLocationDot, faLongArrowRight, faTimer, faTimes, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import React, { ComponentProps, useMemo } from 'react';
import { useState } from 'react';
import { trackEvent } from '../google-tag-manager';
import { useRouter } from 'next/navigation';
import createDemo from '../../lib/client/create-demo';
import { useFindDemoSlots } from '../../lib/client/find-demo-slots';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import Portal from '../portal';
import Link from 'next/link';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calendar);
dayjs.extend(localizedFormat);

function evenlyPickItems<T>(originalArray: T[], desiredLength: number): T[] {
  if (desiredLength >= originalArray.length || desiredLength < 2) {
    // Return the original array if the desired length is larger than the array,
    // or if it's less than 2, because we always want to include the first and last items.
    return originalArray;
  }

  const result: T[] = [originalArray[0]]; // Always include the first item.
  const interval = (originalArray.length - 1) / (desiredLength - 1);

  for (let i = 1; i < desiredLength - 1; i++) {
    // Pick middle elements based on the calculated interval,
    // ensuring the first and last items are always included.
    const pickIndex = Math.round(i * interval);
    result.push(originalArray[pickIndex]);
  }

  result.push(originalArray[originalArray.length - 1]); // Always include the last item.

  return result;
}

const groupSlotsByDay = (slots: Array<{ startTime: Date; endTime: Date; }>) => {
  const groupedSlots = slots.reduce<Record<string, Array<{ startTime: Date; endTime: Date; }>>>((acc, slot) => {
    const key = dayjs(slot.startTime).format('YYYY-MM-DD');

    acc[key] = [...(acc[key] || []), slot];

    return acc;
  }, {});

  return groupedSlots;
};

const truncateSlots = (slots: Array<{ startTime: Date; endTime: Date; }>) => {
  return evenlyPickItems(slots, 5);
};

const filterSlots = (slots: Array<{ startTime: Date; endTime: Date; }>) => {
  const slotsInZone = slots.filter(({ startTime }) => {
    const hour = dayjs(startTime).hour();
    return hour >= 6 && hour < 19;
  });

  const truncatedSlots = Object.values(groupSlotsByDay(slotsInZone)).reduce((acc, slots) => {
    return [...acc, ...truncateSlots(slots)]
  }, []);

  return truncatedSlots;
};

interface BookDemoModalProps {
  open: boolean;
  onClose: () => void;
}

const generateDaysOfMonth = (date: dayjs.Dayjs) => {
  return Array.from({ length: date.daysInMonth() }, (_, i) => date.date(i + 1).startOf('day'));
};

const DatePicker: React.FC<{
  selectedDate?: Date;
  onDateClick: (date: Date) => void;
  slots?: Array<{ startTime: Date; endTime: Date; }>;
}> = (props) => {
  const { selectedDate, onDateClick } = props;

  const { data, isLoading } = useFindDemoSlots({});
  const slots = data?.data ? filterSlots(data.data) : [];

  const now = useMemo(() => dayjs().startOf('day'), []);
  const minDate = now;
  const maxDate = useMemo(() => now.add(2, 'weeks'), [now]);

  const [selectedMonth, setSelectedMonth] = useState(now);

  const prevMonth = (() => {
    const month = selectedMonth.subtract(1, 'month');
    if (month.isBefore(minDate, 'month')) { return; }

    return month;
  })();

  const nextMonth = (() => {
    const month = selectedMonth.add(1, 'month');
    if (month.isAfter(maxDate, 'month')) { return; }

    return month;
  })();

  const days = generateDaysOfMonth(selectedMonth);

  const handleMonthBackClick = () => {
    if (!prevMonth) { return; }

    setSelectedMonth(prevMonth);
  };

  const handleMonthNextClick = () => {
    if (!nextMonth) { return; }

    setSelectedMonth(nextMonth);
  };

  return (
    <div>
      <div className="grid grid-cols-[auto_1fr_auto] justify-between items-center mb-4 gap-2">
        <button type="button" className={`btn btn-sm btn-ghost ${prevMonth ? '' : 'invisible'}`} onClick={handleMonthBackClick}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <p className="text-center">
          {selectedMonth.format('MMMM YYYY')}
        </p>
        <button type="button" className={`btn btn-sm btn-ghost ${nextMonth ? '' : 'invisible'}`} onClick={handleMonthNextClick}>
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </div>

      <div className="grid grid-cols-7 grid-rows-[repeat(7,_minmax(0,_1fr))] items-center justify-center text-center aspect-1">
        <div>M</div>
        <div>T</div>
        <div>W</div>
        <div>T</div>
        <div>F</div>
        <div>S</div>
        <div>S</div>

        {(() => {
          const isDateAvailable = (date: dayjs.Dayjs) => {
            if (isLoading) { return false; }

            return slots.some((slot) => date.isSame(slot.startTime, 'day'));
          };

          return days.map((d) => {
            const colStart = (() => {
              switch (d.day()) {
                case 0: return 'col-start-7';
                case 1: return 'col-start-1';
                case 2: return 'col-start-2';
                case 3: return 'col-start-3';
                case 4: return 'col-start-4';
                case 5: return 'col-start-5';
                case 6: return 'col-start-6';
              }
            })();

            const selected = selectedDate && d.isSame(selectedDate, 'day');
            const disabled = d.isBefore(minDate, 'day') || d.isAfter(maxDate, 'day') || !isDateAvailable(d);
            const isToday = d.isSame(now, 'day');
            const highlightPink = isToday && !selected;

            return (
              <button key={d.valueOf()} className={`btn btn-sm h-full ${selected ? 'btn-accent' : 'btn-ghost'} ${colStart} ${disabled ? 'pointer-events-none opacity-20' : ''} ${highlightPink ? 'text-accent' : ''}`} onClick={() => {
                onDateClick(d.toDate());
              }}>
                {d.format('D')}
              </button>
            );
          })
        })()}
      </div>
    </div>
  );
};

const TimePicker: React.FC<{
  selectedDate?: Date;
  onTimeClick: (date: Date) => void;
}> = (props) => {
  const { selectedDate, onTimeClick } = props;

  const date = selectedDate ? dayjs(selectedDate) : undefined;

  const { data, isLoading } = useFindDemoSlots({});

  if (isLoading) {
    return (
      <div className="grid place-items-center py-8">
        <FontAwesomeIcon icon={faSpinnerThird} spin size="2x" className="text-info" />
      </div>
    );
  }

  const allSlots = data?.data ?? [];
  const slotsInTimezone = filterSlots(allSlots);

  const filteredSlots = date ? (
    slotsInTimezone.filter((slot) => date.isSame(slot.startTime, 'day')) ?? []
  ) : (
    slotsInTimezone.slice(0, 5)
  );

  const groupedSlots = filteredSlots.reduce<Record<string, Array<{ startTime: Date; endTime: Date; }>>>((acc, slot) => {
    const key = dayjs(slot.startTime).format('YYYY-MM-DD');

    acc[key] = [...(acc[key] || []), slot];

    return acc;
  }, date ? ({
    [date.format('YYYY-MM-DD')]: [],
  }) : {}) ?? {};

  return (
    <div className="space-y-6">
      {Object.entries(groupedSlots).map(([day, slots]) => {
        const dayAsDate = dayjs(day);

        return (
          <div key={day}>
            <p className="font-bold text-center mb-4">
              {dayAsDate.calendar(null, {
                sameDay: '[Today]',
                nextDay: '[Tomorrow]',
                nextWeek: 'll',
                lastDay: 'll',
                lastWeek: 'll',
                sameElse: 'll',
              })}
            </p>

            {(() => {
              if (slots.length === 0) {
                return (
                  <div className="flex justify-center py-5">
                    No available time slots.
                  </div>
                );
              }

              return (
                <div className="flex flex-col gap-3">
                  {slots.map((slot) => {
                    const time = dayjs(slot.startTime);

                    const handleClick = () => {
                      onTimeClick(time.toDate());
                    };

                    return (
                      <button key={time.valueOf()} type="button" className="btn btn-primary btn-outline" onClick={handleClick}>
                        <span>
                          {time.format('LT')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
};

const DetailsForm: React.FC<{
  selectedTime: Date;
  onBack: () => void;
  onSubmit: (details: {
    firstName: string,
    lastName: string,
    email: string,
    mobile: string,
    companyName: string,
  }) => Promise<void>;
}> = (props) => {
  const { selectedTime, onBack, onSubmit } = props;

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    companyName: '',
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    setDetails((s) => ({
      ...s,
      [name]: value,
    }));
  };

  const handleBack = () => {
    if (loading) { return; }

    onBack();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) { return; }

    setLoading(true);
    void onSubmit(details).finally(() => {
      setLoading(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-slate-50 rounded-xl px-4 py-2 grid gap-1 md:grid-cols-[auto_auto] mb-4">
        <p className="font-medium">
          <FontAwesomeIcon icon={faCalendar} className="text-slate-500 mr-2" fixedWidth />
          {dayjs(selectedTime).format('LL')}
        </p>

        <p className="font-medium">
          <FontAwesomeIcon icon={faClock} className="text-slate-500 mr-2" fixedWidth />
          {dayjs(selectedTime).format('LT')}
        </p>

        <p className="font-medium">
          <FontAwesomeIcon icon={faTimer} className="text-slate-500 mr-2" fixedWidth />
          45 minutes
        </p>

        <p className="font-medium">
          <FontAwesomeIcon icon={faLocationDot} className="text-slate-500 mr-2" fixedWidth />
          Zoom
        </p>
      </div>

      <div className="md:grid md:grid-cols-2 md:gap-4">
        <div className="form-control mb-1">
          <label className="label">
            <span className="label-text font-bold text-slate-700">
              First name
            </span>
          </label>
          <input
            required
            autoComplete="given-name"
            className="input input-bordered"
            placeholder="First name"
            value={details.firstName}
            onChange={handleInputChange}
            name="firstName"
          />
        </div>

        <div className="form-control mb-1">
          <label className="label">
            <span className="label-text font-bold text-slate-700">
              Last name
            </span>
          </label>
          <input
            required
            autoComplete="family-name"
            className="input input-bordered"
            placeholder="First name"
            value={details.lastName}
            onChange={handleInputChange}
            name="lastName"
          />
        </div>
      </div>

      <div className="form-control mb-1">
        <label className="label">
          <span className="label-text font-bold text-slate-700">
            Email
          </span>
        </label>
        <input
          required
          type="email"
          autoComplete="email"
          className="input input-bordered"
          placeholder="Email"
          value={details.email}
          onChange={handleInputChange}
          name="email"
        />
      </div>

      <div className="form-control mb-1">
        <label className="label">
          <span className="label-text font-bold text-slate-700">
            Mobile
          </span>
        </label>
        <input
          required
          type="tel"
          autoComplete="tel"
          className="input input-bordered"
          placeholder="Mobile"
          value={details.mobile}
          onChange={handleInputChange}
          name="mobile"
        />
      </div>

      <div className="form-control mb-1">
        <label className="label">
          <span className="label-text font-bold text-slate-700">
            Company name
          </span>
        </label>
        <input
          type="text"
          autoComplete="organization"
          className="input input-bordered"
          placeholder="Company name"
          value={details.companyName}
          onChange={handleInputChange}
          name="companyName"
        />
      </div>

      <div className="flex justify-between border-t pt-4 mt-4">
        <button type="button" className="btn btn-sm btn-ghost gap-2" onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>

        <button type="submit" className={`btn btn-primary ${loading ? 'loading' : ''}`}>
          Confirm & Book
        </button>
      </div>
    </form>
  );
};

type BookDemoFormState = {
  stage: 'init';
} | {
  stage: 'date';
} | {
  stage: 'time';
  selectedDate: Date;
} | {
  stage: 'details';
  selectedTime: Date;
};

export const BookDemoForm = () => {
  const router = useRouter();

  const { data, mutate, isLoading } = useFindDemoSlots({});

  const slots = data?.data ? filterSlots(data.data) : [];
  const earliestDate = dayjs(slots[0]?.startTime).startOf('day');

  const [state, _setState] = useState<BookDemoFormState & {
    prev?: BookDemoFormState;
  }>(() => ({
    stage: 'init',
  }));

  const setState: typeof _setState = (actionOrState) => {
    _setState((s) => {
      const nextState = typeof actionOrState === 'function' ? actionOrState(s) : actionOrState;

      return { ...nextState, prev: s };
    });
  };

  if ('selectedDate' in state) {
    if (earliestDate.isAfter(state.selectedDate)) {
      setState((s) => ({
        ...s,
        selectedDate: earliestDate.toDate(),
      }));
    }
  }

  switch (state.stage) {
    case 'init':
    case 'date':
    case 'time': {
      const handleDateClick = (date: Date) => {
        setState({
          stage: 'time',
          selectedDate: date,
        });
      };

      const handleTimeClick = (date: Date) => {
        setState({
          stage: 'details',
          selectedTime: date,
        });
      };

      const date = 'selectedDate' in state ? state.selectedDate : earliestDate.toDate();

      return (
        <div>
          {state.stage === 'init' ? (
            <div className="sm:hidden">
              <TimePicker
                onTimeClick={handleTimeClick}
              />
              <button type="submit" className="btn gap-2 btn-link btn-block mt-4" onClick={() => setState({ stage: 'date' })}>
                <FontAwesomeIcon icon={faCalendar} />
                Pick a different day
              </button>
            </div>
          ) : null}

          <div className="grid sm:grid-cols-5 gap-4">
            <div className={`relative overflow-hidden bg-slate-50 rounded-xl p-2 col-span-3 ${state.stage !== 'date' ? 'hidden sm:block' : ''}`}>
              <div className="sm:hidden">
                <DatePicker
                  onDateClick={handleDateClick}
                  slots={slots}
                />
              </div>

              <div className="hidden sm:block">
                <DatePicker
                  selectedDate={date}
                  onDateClick={handleDateClick}
                  slots={slots}
                />
              </div>

              {isLoading ? (
                <div className="absolute inset-0 grid place-items-center bg-slate-50">
                  <FontAwesomeIcon icon={faSpinnerThird} spin size="3x" className="text-info" />
                </div>
              ) : null}
            </div>

            <div className={`col-span-2 pt-2 ${state.stage !== 'time' ? 'hidden sm:block' : ''}`}>
              <TimePicker
                selectedDate={date}
                onTimeClick={handleTimeClick}
              />
            </div>
          </div>

          {(() => {
            switch (state.stage) {
              case 'date': return (
                <div className="flex border-t pt-4 mt-4 sm:hidden">
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost gap-2"
                    onClick={() => setState({ stage: 'init' })}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Back
                  </button>
                </div>
              );

              case 'time': return (
                <div className="flex border-t pt-4 mt-4 sm:hidden">
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost gap-2"
                    onClick={() => setState({ stage: 'date' })}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Back
                  </button>
                </div>
              );
            }
          })()}
        </div>
      );
    }

    case 'details': {
      const handleBack = () => {
        setState((s) => (s.prev ?? {
          stage: 'time',
          selectedDate: state.selectedTime,
        }));
      };

      const handleSubmit: ComponentProps<typeof DetailsForm>['onSubmit'] = async (details) => {
        await createDemo({
          details,
          date: state.selectedTime,
        });

        trackEvent({
          event: 'demo_booked',
        });

        const searchParams = new URLSearchParams({
          email: details.email,
          firstname: details.firstName,
          lastname: details.lastName,
          company: details.companyName,
        });

        router.push(`/demo-booked?${searchParams.toString()}`);

        void mutate();
      };

      return (
        <DetailsForm
          selectedTime={state.selectedTime}
          onBack={handleBack}
          onSubmit={handleSubmit}
        />
      );
    }

    default: return null;
  }
};

interface BookDemoModalProps {
  open: boolean;
  onClose: () => void;
}

export const BookDemoModal: React.FC<BookDemoModalProps> = (props) => {
  const { open, onClose } = props;

  if (!open) { return null; }

  return (
    <Portal>
      <div className="modal modal-open text-left">
        <div className="modal-box">
          <div className="flex justify-between mb-4">
            <div>
              <p className="font-medium text-lg text-left text-slate-700">
                Book your demo
              </p>
            </div>

            <button type="button" className="btn btn-ghost text-gray-400 -mt-4 -mr-4" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} size="2x" />
            </button>
          </div>

          <div className="flex-1">
            <BookDemoForm />
          </div>
        </div>
      </div>
    </Portal>
  );
};

interface BookDemoCtaProps {
  text?: React.ReactNode;
  showWatchDemo?: boolean;
}

const BookDemoCta: React.FC<BookDemoCtaProps> = (props) => {
  const {
    text = 'Want to find out more, or ready to get signed up?',
    showWatchDemo = false,
  } = props;

  const [openModal, setOpenModal] = useState<null | 'book' | 'watch'>(null);

  const handleBookOpen = () => {
    trackEvent({
      event: 'book_demo_click',
    });

    setOpenModal('book');
  };

  const handleBookClose = () => {
    if (showWatchDemo) {
      setOpenModal('watch');
    } else {
      setOpenModal(null);
    }
  };

  const handleWatchClose = () => {
    setOpenModal(null);
  };

  return (
    <>
      <button type="button" className="block p-10 rounded-3xl bg-primary text-primary-content shadow-xl shadow-primary/40 group" onClick={handleBookOpen}>
        <p className="text-2xl font-semibold text-center">
          {text}
        </p>

        <div className="flex justify-center mt-10">
          <p className="btn btn-ghost btn-lg rounded-full group-hover:text-primary group-hover:bg-primary-content w-full sm:btn-wide">
            Book a demo

            <FontAwesomeIcon icon={faLongArrowRight} className="ml-2 transition-transform group-hover:translate-x-1" />
          </p>
        </div>
      </button>

      <BookDemoModal open={openModal === 'book'} onClose={handleBookClose} />

      {openModal === 'watch' ? (
        <Portal>
          <div className="modal modal-open">
            <div className="modal-box overflow-auto md:h-auto">
              <div className="flex justify-end">
                <button type="button" className="btn btn-ghost text-gray-400 -mt-4 -mr-4" onClick={handleWatchClose}>
                  <FontAwesomeIcon icon={faXmark} size='2x' />
                </button>
              </div>

              <p className="text-2xl text-center mb-6 font-medium">
                Not ready to speak to us?
              </p>

              <p className="text-lg text-center mb-10 text-slate-600 max-w-xs mx-auto">
                Watch the full product tour and find out if The Salon App is for you.
              </p>

              <div className="flex justify-center">
                <Link href="/watch-demo/play" className="btn btn-primary btn-lg rounded-full text-base w-full max-w-xs">
                  Watch the demo
                </Link>
              </div>
            </div>
          </div>
        </Portal>
      ) : null}
    </>
  );
};

export default BookDemoCta;
